import React from 'react';
import './ProfessionalForeignObject.css';

interface Experience {
    id: string;
    company: string;
    role: string;
    duration: string;
    description: string[];
    technologies: string[];
}

interface ProfessionalForeignObjectProps {
    bounds: {
        x: number;
        y: number;
        width: number;
        height: number;
    };
    groupName: string;
    displayText: string;
    isHovered: boolean;
    isSelected: boolean;
    clipBounds?: boolean;
    leftMargin?: number;
    rightMargin?: number;
    topMargin?: number;
}

// Sample professional experience data
const experiences: Experience[] = [
    {
        id: 'exp1',
        company: 'Amazon (AWS)',
        role: 'Software Engineer Intern',
        duration: 'May 2025 - Aug 2025',
        description: [
            'Developed first frontend for Amazon Intent Driven Network CLI to visualize ML/HPC AWS network changes',
            'Engineered scalable visualization of 50K+ node networks using React, ReactFlow, and Cloudscape',
            'Built database feature to query entire history of changes made to Amazon network chronologically',
            'Reduced time of critical workflows by as much as 87%, including full network traversal (16 min down to 2 min)',
            'Deployed via end-end CDK pipeline, application is actively used in production by on call teams'
        ],
        technologies: ['React', 'ReactFlow', 'Cloudscape', 'Java', 'SQL', 'CDK']
    },
    {
        id: 'exp2',
        company: 'AMD',
        role: 'AI Framework Engineer Intern',
        duration: 'Jan 2025 - May 2025',
        description: [
            'Productionized Quarot quantization research in vLLM, reducing Llama-3 8B perplexity from 250 to 7.2 with only 5% latency overhead',
            'Designed customized fused kernel for optimized fast hadamard transform rotation for AMD Instinct GPU',
            'Scaled algorithm for larger 70B, 400B models via PyTorch distributed tensor parallelism for multi-GPU systems',
            'Delivered open source contribution to vLLM and SGLang, that can be used out-of-the-box'
        ],
        technologies: ['PyTorch', 'vLLM', 'SGLang', 'C++', 'CUDA', 'GPU Programming']
    },
    {
        id: 'exp4',
        company: 'Sandia National Labs',
        role: 'R&D Intern',
        duration: 'May 2024 - Aug 2024',
        description: [
            'Deploy ResNet, Vision Transformer architectures to improve computer vision algorithm for mission application',
            'Built distributed training and testing pipeline optimized for multi GPU system to maximize throughput'
        ],
        technologies: ['PyTorch', 'ResNet', 'Vision Transformers', 'Computer Vision', 'Multi-GPU', 'Python']
    },
    {
        id: 'exp5',
        company: 'Sandia National Labs',
        role: 'R&D Co-op',
        duration: 'May 2023 - Dec 2023',
        description: [
            'Authored grant, secured $100k of funds, brought major legacy C++ simulation codebase to modern standard',
            'Reduced total runtime by 35% by using multithreading in the simulation rendering loop',
            'Streamlined pipeline to ingest sensor data to simulation, writing scripts to automate pre and post-processing',
            'Significantly improved codebase usability, doubling user base from 5 to 10 internal researchers'
        ],
        technologies: ['C++', 'Multithreading', 'Simulation', 'Data Processing', 'Performance Optimization']
    }
];

const ProfessionalForeignObject: React.FC<ProfessionalForeignObjectProps> = ({ 
    bounds, 
    groupName, 
    displayText, 
    isHovered, 
    isSelected,
    clipBounds = true,
    leftMargin = 32,
    rightMargin = 32,
    topMargin = 0
}) => {

    // Calculate adjusted bounds when clipping is enabled and component is selected
    const getAdjustedBounds = () => {
        if (!isSelected || !clipBounds) {
            return bounds;
        }
        
        return {
            x: bounds.x + leftMargin,
            y: bounds.y + topMargin,
            width: bounds.width - leftMargin - rightMargin,
            height: bounds.height - topMargin
        };
    };

    const adjustedBounds = getAdjustedBounds();

    if (!isSelected) {
        return (
            <div className="professional-foreign-object header-only">
                <div className="header-text">{displayText}</div>
            </div>
        );
    }

    return (
        <div 
            className="foreign-object-base expanded"
            style={clipBounds ? {
                width: adjustedBounds.width,
                height: adjustedBounds.height,
                marginLeft: leftMargin,
                marginTop: topMargin,
                marginRight: rightMargin
            } : undefined}
        >
            <div className="header-text">{displayText}</div>
            <div className="main-container">
                
                {experiences.map((exp) => (
                    <div key={exp.id} className="card-item">
                        <div className="card-header expanded">
                            <div className="card-main">
                                <div className="company-role">
                                    <span className="company">{exp.company}</span>
                                    <span className="role">{exp.role}</span>
                                </div>
                                <div className="duration">{exp.duration}</div>
                            </div>
                        </div>
                        
                        {/* Always show details – never collapsed */}
                        <div className="card-details">
                            <div className="description">
                                {exp.description.map((item, index) => (
                                    <div key={index} className="description-item">
                                        • {item}
                                    </div>
                                ))}
                            </div>
                            <div className="technologies">
                                <div className="tag-label">Technologies:</div>
                                <div className="tag-container">
                                    {exp.technologies.map((tech, index) => (
                                        <span key={index} className="tag-base">{tech}</span>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ProfessionalForeignObject;
